import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { reviewApi, type QueueRow, type QueueView } from "../api/review";
import { ALL_STEPS, FunnelBar } from "./FunnelBar";
import { ReviewerGate } from "./ReviewerGate";
import { timeAgo } from "./time";

type Filters = { boost: boolean; watch: boolean; old: boolean };

const STEP_LABELS: Record<string, string> = {
  signed_in: "Signed in",
  cv_added: "CV added",
  agent_connected: "Agent connected",
  start_here_used: "Used start_here",
  profile_complete: "Profile complete",
  questions_opened: "Questions opened",
  submitted: "Submitted",
};

function visible(row: QueueRow, f: Filters): boolean {
  return (
    (!f.boost || (row.boost ?? 0) > 0) &&
    (!f.watch || (row.watch ?? 0) > 0) &&
    (!f.old || (row.days_waiting ?? 0) > 5)
  );
}

function SubmittedTable({ rows }: { rows: QueueRow[] }) {
  return (
    <table>
      <thead>
        <tr>
          <th>ID</th>
          <th>Name</th>
          <th>Submitted</th>
          <th>Waiting</th>
          <th title="Profile and answer flags; telemetry flags are on the candidate page">
            Boost / watch
          </th>
          <th>Reviews</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.public_id}>
            <td>
              <Link to={`/review/${row.public_id}`}>{row.public_id}</Link>
            </td>
            <td>{row.name}</td>
            <td>{row.submitted_at ? new Date(row.submitted_at).toLocaleString() : "—"}</td>
            <td>{row.days_waiting ?? "—"} d</td>
            <td>
              {row.boost ?? "—"} / {row.watch ?? "—"}
            </td>
            <td>{row.reviews}</td>
            <td>{row.status.replace("_", " ")}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ProgressTable({ rows }: { rows: QueueRow[] }) {
  return (
    <table>
      <thead>
        <tr>
          <th>ID</th>
          <th>Name</th>
          <th>Furthest step</th>
          <th>Reached</th>
          <th>Agent via</th>
          <th>Started</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.public_id}>
            <td>
              <Link to={`/review/${row.public_id}`}>{row.public_id}</Link>
            </td>
            <td>{row.name}</td>
            <td>{STEP_LABELS[row.step] ?? row.step}</td>
            <td title={row.step_at ? new Date(row.step_at).toLocaleString() : "time unknown"}>
              {timeAgo(row.step_at)}
            </td>
            <td>{row.agent_via ?? "—"}</td>
            <td>{new Date(row.started_at).toLocaleDateString()}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Queue() {
  const [step, setStep] = useState("submitted");
  const [view, setView] = useState<QueueView>("awaiting");
  const [filters, setFilters] = useState<Filters>({ boost: false, watch: false, old: false });
  const queue = useQuery({
    queryKey: ["review", "queue", step, view],
    queryFn: () => reviewApi.queue(view, step),
  });
  const funnel = useQuery({ queryKey: ["review", "funnel"], queryFn: reviewApi.funnel });
  const spend = useQuery({ queryKey: ["review", "spend"], queryFn: reviewApi.llmSpend });
  const toggle = (key: keyof Filters) => setFilters({ ...filters, [key]: !filters[key] });
  const submittedOnly = step === "submitted";
  const rows = (queue.data ?? []).filter((row) => !submittedOnly || visible(row, filters));

  return (
    <main className="page wide">
      <h1>Review queue</h1>
      <p className="muted">
        No ranking and no score: open each application and read the record.
        {spend.data &&
          ` LLM use, last 7 days: ${spend.data.calls_7d} calls, $${spend.data.cost_usd_7d.toFixed(2)}.`}
      </p>
      {funnel.data && <FunnelBar funnel={funnel.data} selected={step} onSelect={setStep} />}

      <h2>
        {step === ALL_STEPS
          ? "Everyone"
          : submittedOnly
            ? "Submitted"
            : `Furthest step: ${STEP_LABELS[step] ?? step}`}
      </h2>
      {submittedOnly ? (
        <>
          <div className="actions" role="group" aria-label="Queue">
            {(["awaiting", "decided", "all"] as const).map((v) => (
              <button
                key={v}
                type="button"
                className={view === v ? "" : "secondary"}
                onClick={() => setView(v)}
              >
                {{ awaiting: "Awaiting review", decided: "Decided", all: "All submitted" }[v]}
              </button>
            ))}
          </div>
          <div className="filters">
            <label className="checkbox">
              <input type="checkbox" checked={filters.boost} onChange={() => toggle("boost")} /> Has
              boost flag
            </label>
            <label className="checkbox">
              <input type="checkbox" checked={filters.watch} onChange={() => toggle("watch")} /> Has
              watch flag
            </label>
            <label className="checkbox">
              <input type="checkbox" checked={filters.old} onChange={() => toggle("old")} /> Waiting
              &gt; 5 days
            </label>
          </div>
        </>
      ) : (
        <p className="muted">
          Not submitted yet: you can open these to see what's recorded so far, but decisions wait
          for a submission.{" "}
          <button type="button" className="link-button" onClick={() => setStep("submitted")}>
            Back to submitted
          </button>
        </p>
      )}
      {queue.isError && <p role="alert">{queue.error.message}</p>}
      {queue.isSuccess && rows.length === 0 && <p>Nothing here.</p>}
      {rows.length > 0 && (
        <div className="table-scroll">
          {submittedOnly ? <SubmittedTable rows={rows} /> : <ProgressTable rows={rows} />}
        </div>
      )}
    </main>
  );
}

export function ReviewHome() {
  return (
    <ReviewerGate>
      <Queue />
    </ReviewerGate>
  );
}
